import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppHeader from "../components/AppHeader";
import {
    checklistApi,
    flattenErrors,
    getStaff,
} from "../utils/Api";
import { formatBusinessDate, labelize } from "../utils/format";
import { captureCurrentLocation, getCachedLocation } from "../utils/location";

const CHECKLIST_DRAFT_PREFIX = "xpanse_checklist_draft";

function toId(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
}

function collectAnswers(existing) {
    if (!existing || typeof existing !== "object") return [];

    const answers = [];

    if (Array.isArray(existing.answers)) {
        answers.push(...existing.answers);
    }

    if (Array.isArray(existing.categories)) {
        existing.categories.forEach((category) => {
            (category.answers || []).forEach((answer) => {
                answers.push({
                    ...answer,
                    category_id:
                        answer.category_id || category.category_id || category.id,
                });
            });
        });
    }

    return answers;
}

function readExistingSubmissionId(form) {
    if (!form || typeof form !== "object") return null;

    return (
        form.existing_submission?.id ??
        form.existing_submission?.submission_id ??
        form.submission_id ??
        null
    );
}

function readCreatedSubmissionId(payload) {
    if (!payload || typeof payload !== "object") return null;

    return payload.submission?.id ?? payload.submission_id ?? payload.id ?? null;
}

function isFormLocked(formData) {
    if (!formData) return false;

    return (
        Boolean(formData.read_only) ||
        formData.can_edit === false ||
        formData.status === "SUBMITTED" ||
        formData.status === "MISSED"
    );
}

function lockMessage(formData) {
    if (!isFormLocked(formData)) return "";

    if (formData.status === "MISSED") {
        return "This window has closed, so the checklist can no longer be submitted.";
    }

    const owner =
        formData.existing_submission?.submitted_by_name ||
        formData.existing_submission?.submitted_by ||
        formData.submitted_by ||
        "";
    const isOwn =
        formData.is_own_submission ??
        formData.existing_submission?.is_own_submission;

    if (owner && isOwn === false) {
        return `Submitted by ${owner}. This checklist is locked.`;
    }

    if (isOwn === false && (formData.read_only || formData.status === "SUBMITTED")) {
        return "Another staff member already submitted this checklist. It is view only.";
    }

    if (formData.status === "SUBMITTED") {
        return "This checklist is submitted and can only be viewed.";
    }

    return "This checklist is locked and can only be viewed.";
}

export default function ChecklistSubmit() {
    const navigate = useNavigate();
    const routeState = useLocation().state;
    const [searchParams] = useSearchParams();
    const topProgressRef = useRef(null);
    const draftHydratedRef = useRef(false);
    const requestRef = useRef(0);
    const checklistKeyRef = useRef("");

    const categoryId =
        searchParams.get("category_id") ||
        routeState?.categoryId ||
        routeState?.checklistId ||
        "";
    const checklistType =
        searchParams.get("checklist_type") || routeState?.checklistType || "";
    const frequency =
        searchParams.get("frequency") || routeState?.frequency || "";
    const businessDate =
        searchParams.get("business_date") || routeState?.businessDate || "";

    const [items, setItems] = useState([]);
    const [formData, setFormData] = useState(null);
    const [submissionId, setSubmissionId] = useState(null);
    const [location, setLocation] = useState(null);
    const [locationNotice, setLocationNotice] = useState("");
    const [locating, setLocating] = useState(false);
    const [isTopProgressVisible, setIsTopProgressVisible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [banner, setBanner] = useState("");
    const [bannerDetails, setBannerDetails] = useState([]);
    const [actionLoading, setActionLoading] = useState("");
    const [formReady, setFormReady] = useState(false);

    const locked = isFormLocked(formData);

    const activeCategoryId = useMemo(() => {
        const apiCategoryId = formData?.category_id ?? formData?.categories?.[0]?.id;
        return toId(apiCategoryId ?? categoryId);
    }, [formData, categoryId]);

    const draftStorageKey = useMemo(() => {
        const resolvedChecklistType = formData?.checklist_type || checklistType;
        const resolvedFrequency = formData?.frequency || frequency;
        const resolvedBusinessDate = formData?.business_date || businessDate;
        const staffId = getStaff()?.id || "staff";

        if (
            !activeCategoryId ||
            !resolvedChecklistType ||
            !resolvedFrequency ||
            !resolvedBusinessDate
        ) {
            return null;
        }

        return [
            CHECKLIST_DRAFT_PREFIX,
            staffId,
            activeCategoryId,
            resolvedChecklistType,
            resolvedFrequency,
            resolvedBusinessDate,
        ].join(":");
    }, [activeCategoryId, formData, checklistType, frequency, businessDate]);

    checklistKeyRef.current = [
        categoryId,
        checklistType,
        frequency,
        businessDate,
    ].join("|");

    const fetchChecklistForm = useCallback(async ({ soft = false } = {}) => {
        const requestId = ++requestRef.current;

        if (!categoryId || !checklistType || !frequency || !businessDate) {
            setLoading(false);
            setFormReady(false);
            setLoadError(
                "Open this checklist from the home screen so the correct date and type are used."
            );
            return;
        }

        try {
            setLoading(true);
            setLoadError("");
            setFormReady(false);
            draftHydratedRef.current = false;

            const json = await checklistApi.form({
                categoryId,
                checklistType,
                frequency,
                businessDate,
            });

            const data = json.data || {};

            if (requestId !== requestRef.current) return;

            const categories = (Array.isArray(data.categories) ? data.categories : [])
                .slice()
                .sort(
                    (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
                );
            const serverAnswers = collectAnswers(data.existing_submission);
            const answerMap = new Map();

            serverAnswers.forEach((answer) => {
                const taskId = Number(
                    answer.task_id ?? answer.checklist_task_id ?? answer.id
                );

                if (Number.isFinite(taskId)) {
                    answerMap.set(taskId, answer);
                }
            });

            const hasServerSubmission = Boolean(
                data.existing_submission?.id ||
                    data.existing_submission?.submission_id ||
                    serverAnswers.length
            );

            const lockedResponse =
                Boolean(data.read_only) ||
                data.can_edit === false ||
                data.status === "SUBMITTED" ||
                data.status === "MISSED";

            let mappedItems = categories.flatMap((category) => {
                const tasks = Array.isArray(category.tasks)
                    ? category.tasks
                    : Array.isArray(category.checklist_tasks)
                      ? category.checklist_tasks
                      : [];

                return tasks
                    .slice()
                    .sort(
                        (a, b) =>
                            Number(a.sort_order || 0) - Number(b.sort_order || 0)
                    )
                    .map((task) => {
                        const saved = answerMap.get(Number(task.id));
                        const answer = String(
                            saved?.answer || task.answer || ""
                        ).toUpperCase();

                        return {
                            id: toId(task.id),
                            category_id: toId(category.id ?? category.category_id),
                            title: task.task_name,
                            answer: answer === "YES" || answer === "NO" ? answer : "",
                            remark: saved?.remark ?? task.remark ?? "",
                            is_mandatory:
                                task.is_mandatory === 0 ||
                                task.is_mandatory === "0" ||
                                task.is_mandatory === false
                                    ? 0
                                    : 1,
                            sort_order: Number(task.sort_order || 0),
                        };
                    });
            });

            const resolvedCategoryId = toId(
                data.category_id ?? categories[0]?.id ?? categoryId
            );
            const resolvedDraftKey = [
                CHECKLIST_DRAFT_PREFIX,
                getStaff()?.id || "staff",
                resolvedCategoryId,
                data.checklist_type || checklistType,
                data.frequency || frequency,
                data.business_date || businessDate,
            ].join(":");

            if (!hasServerSubmission && !lockedResponse) {
                try {
                    const cachedDraft = localStorage.getItem(resolvedDraftKey);

                    if (cachedDraft) {
                        const parsedDraft = JSON.parse(cachedDraft);
                        const draftAnswers = new Map(
                            (parsedDraft?.items || []).map((item) => [
                                Number(item.id),
                                item,
                            ])
                        );

                        mappedItems = mappedItems.map((item) => {
                            const draftItem = draftAnswers.get(Number(item.id));

                            if (!draftItem) return item;

                            const answer = String(draftItem.answer || "").toUpperCase();

                            return {
                                ...item,
                                answer: answer === "YES" || answer === "NO" ? answer : "",
                                remark: draftItem.remark || "",
                            };
                        });
                    }
                } catch {
                    localStorage.removeItem(resolvedDraftKey);
                }
            }

            if (requestId !== requestRef.current) return;

            setFormData({ ...data, categories });
            setSubmissionId(readExistingSubmissionId(data));
            setItems(mappedItems);
            setFormReady(true);
            draftHydratedRef.current = true;
        } catch (error) {
            if (requestId !== requestRef.current || error?.status === 401) return;

            if (soft) {
                setFormReady(true);
                draftHydratedRef.current = true;
                return;
            }

            setLoadError(error?.message || "Unable to load this checklist.");
            setFormReady(false);
        } finally {
            if (requestId === requestRef.current) {
                setLoading(false);
            }
        }
    }, [categoryId, checklistType, frequency, businessDate]);

    useEffect(() => {
        fetchChecklistForm();
    }, [fetchChecklistForm]);

    useEffect(() => {
        if (
            !draftHydratedRef.current ||
            !formReady ||
            !draftStorageKey ||
            locked
        ) {
            return;
        }

        const hasDraftContent = items.some(
            (item) => item.answer || item.remark.trim()
        );

        if (!hasDraftContent) {
            localStorage.removeItem(draftStorageKey);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            localStorage.setItem(
                draftStorageKey,
                JSON.stringify({
                    category_id: activeCategoryId,
                    checklist_type: formData?.checklist_type || checklistType,
                    frequency: formData?.frequency || frequency,
                    business_date: formData?.business_date || businessDate,
                    updated_at: new Date().toISOString(),
                    items: items.map(({ id, category_id, answer, remark }) => ({
                        id,
                        category_id,
                        answer,
                        remark,
                    })),
                })
            );
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [
        items,
        formReady,
        locked,
        formData,
        draftStorageKey,
        activeCategoryId,
        checklistType,
        frequency,
        businessDate,
    ]);

    useEffect(() => {
        const cached = getCachedLocation();

        if (cached) setLocation(cached);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (!topProgressRef.current) return;

            const rect = topProgressRef.current.getBoundingClientRect();
            setIsTopProgressVisible(rect.bottom > 100);
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const answeredCount = useMemo(
        () => items.filter((item) => item.answer === "YES" || item.answer === "NO").length,
        [items]
    );

    const progress = items.length
        ? Math.round((answeredCount / items.length) * 100)
        : 0;

    const canSubmit = useMemo(() => {
        if (!items.length || locked) return false;

        return items.every((item) => {
            if (item.is_mandatory === 1 && item.answer !== "YES" && item.answer !== "NO") {
                return false;
            }

            if (item.answer === "NO" && !item.remark.trim()) return false;

            return true;
        });
    }, [items, locked]);

    const updateAnswer = (id, answer) => {
        if (locked) return;

        setItems((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                          ...item,
                          answer: item.answer === answer ? "" : answer,
                      }
                    : item
            )
        );
    };

    const updateRemark = (id, remark) => {
        if (locked) return;

        setItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, remark } : item))
        );
    };

    const focusItem = (id) => {
        document.getElementById(`task-${id}`)?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    };

    const ensureLocation = async () => {
        setLocating(true);
        setLocationNotice("");

        try {
            const captured = await captureCurrentLocation({ maximumAge: 0 });

            if (
                typeof captured.latitude !== "number" ||
                typeof captured.longitude !== "number" ||
                !captured.name
            ) {
                throw new Error(
                    "GPS location and address are required before saving."
                );
            }

            setLocation(captured);
            return captured;
        } finally {
            setLocating(false);
        }
    };

    const saveChecklist = async (status) => {
        if (locked || actionLoading || locating) return;

        if (!activeCategoryId) {
            toast.error("Checklist category is missing. Please reopen it from home.");
            return;
        }

        const answeredItems = items.filter(
            (item) => item.answer === "YES" || item.answer === "NO"
        );

        if (status === "DRAFT" && answeredItems.length === 0) {
            toast.error("Answer at least one task before saving a draft.");
            return;
        }

        if (status === "SUBMITTED") {
            const missingMandatoryItem = items.find(
                (item) =>
                    item.is_mandatory === 1 &&
                    item.answer !== "YES" &&
                    item.answer !== "NO"
            );

            if (missingMandatoryItem) {
                toast.error(
                    `Please answer "${missingMandatoryItem.title}" before submitting.`
                );
                focusItem(missingMandatoryItem.id);
                return;
            }

            const invalidItem = items.find(
                (item) => item.answer === "NO" && !item.remark.trim()
            );

            if (invalidItem) {
                toast.error(`Please enter a remark for "${invalidItem.title}".`);
                focusItem(invalidItem.id);
                return;
            }
        }

        try {
            setActionLoading(status);
            setBanner("");
            setBannerDetails([]);

            const captured = await ensureLocation();
            const payload = {
                category_id: activeCategoryId,
                checklist_type: formData?.checklist_type || checklistType,
                frequency: formData?.frequency || frequency,
                business_date: formData?.business_date || businessDate,
                status,
                latitude: captured.latitude,
                longitude: captured.longitude,
                address: captured.name,
                answers: answeredItems.map((item) => ({
                    category_id: activeCategoryId,
                    task_id: toId(item.id),
                    answer: item.answer,
                    remark: item.answer === "NO" ? item.remark.trim() : null,
                })),
            };

            const json = submissionId
                ? await checklistApi.updateSubmission(submissionId, payload)
                : await checklistApi.createSubmission(payload);

            const nextId = readCreatedSubmissionId(json?.data);
            const savedKey = checklistKeyRef.current;

            if (nextId) setSubmissionId(nextId);

            toast.success(
                json.message ||
                    (status === "DRAFT"
                        ? "Draft saved successfully."
                        : "Checklist submitted successfully.")
            );

            if (draftStorageKey) {
                localStorage.removeItem(draftStorageKey);
            }

            if (status === "SUBMITTED") {
                draftHydratedRef.current = false;
                navigate("/home", { replace: true });
                return;
            }

            await fetchChecklistForm({ soft: true });

            if (nextId && checklistKeyRef.current === savedKey) {
                setSubmissionId((current) => current || nextId);
            }
        } catch (error) {
            if (error?.status === 401) return;

            const details = flattenErrors(error?.errors);
            const message =
                details[0] ||
                error?.message ||
                (status === "DRAFT"
                    ? "Unable to save draft."
                    : "Unable to submit checklist.");

            setBanner(message);
            setBannerDetails(details);
            toast.error(message);

            if (error?.status === 409) {
                setFormData((current) =>
                    current
                        ? { ...current, read_only: true, can_edit: false, status: "SUBMITTED" }
                        : current
                );
            }

            if (error?.status === 403 || error?.status === 409) {
                fetchChecklistForm();
            }
        } finally {
            setActionLoading("");
        }
    };

    const previewLocation = async () => {
        try {
            setLocationNotice("");
            const captured = await ensureLocation();
            setLocation(captured);
        } catch (error) {
            setLocationNotice(error?.message || "Unable to capture location.");
        }
    };

    const remainingTasks = items.length - answeredCount;
    const showBottomProgress = !isTopProgressVisible && remainingTasks > 0 && !locked;
    const notice = lockMessage(formData);

    if (loading) {
        return (
            <main className="min-h-screen bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex justify-center">
                <section className="w-full max-w-[430px] min-h-screen bg-[#F6FBFD]">
                    <AppHeader />
                    <div className="px-5 pt-16 text-center">
                        <div className="w-9 h-9 mx-auto rounded-full border-[3px] border-[#09A5F7]/20 border-t-[#09A5F7] animate-spin" />
                        <p className="mt-3 text-[#09A5F7] font-black">
                            Loading checklist...
                        </p>
                    </div>
                </section>
            </main>
        );
    }

    if (loadError) {
        return (
            <main className="min-h-screen bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex justify-center">
                <section className="w-full max-w-[430px] min-h-screen bg-[#F6FBFD]">
                    <AppHeader />
                    <div className="px-5 pt-8">
                        <button
                            type="button"
                            onClick={() => navigate("/home")}
                            className="text-[#09A5F7] text-sm font-extrabold mb-5"
                        >
                            ← Back
                        </button>
                        <div className="rounded-[24px] bg-red-50 border border-red-100 p-5">
                            <p className="text-sm font-extrabold text-red-500">
                                {loadError}
                            </p>
                            <button
                                type="button"
                                onClick={() => fetchChecklistForm()}
                                className="mt-4 px-5 h-10 rounded-[14px] bg-red-500 text-white text-xs font-extrabold"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex justify-center font-sans">
            <section className="relative w-full max-w-[430px] min-h-screen bg-[#F6FBFD] shadow-[0_24px_70px_rgba(14,73,97,0.26)] overflow-hidden">
                <AppHeader />

                <div className="px-5 pt-5 pb-32">
                    <button
                        type="button"
                        onClick={() => navigate("/home")}
                        className="text-[#09A5F7] text-sm font-extrabold mb-5 active:scale-95 transition"
                    >
                        ← Back
                    </button>

                    <div className="rounded-[30px] bg-gradient-to-br from-white to-[#EAF8FE] border border-[#DDF0F6] p-5 shadow-[0_18px_42px_rgba(14,73,97,0.12)]">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#09A5F7] font-black">
                            {labelize(formData?.checklist_type)} Checklist
                            {formData?.frequency ? ` · ${labelize(formData.frequency)}` : ""}
                        </p>
                        <h1 className="text-[23px] font-black text-[#0E4961] leading-tight mt-2">
                            {formData?.category_name || "Checklist"}
                        </h1>

                        <div className="mt-5 flex items-center justify-between gap-3">
                            <div className="rounded-2xl bg-white px-4 py-3 border border-[#DDF0F6] flex-1">
                                <p className="text-[11px] text-[#6E8794] font-bold">
                                    Business Date
                                </p>
                                <p className="text-sm text-[#0E4961] font-black mt-1">
                                    {formatBusinessDate(formData?.business_date)}
                                </p>
                            </div>
                            <div className="rounded-2xl bg-white px-4 py-3 border border-[#DDF0F6] flex-1">
                                <p className="text-[11px] text-[#6E8794] font-bold">
                                    Status
                                </p>
                                <p className="text-sm text-[#0E4961] font-black mt-1">
                                    {labelize(formData?.status) || "Pending"}
                                </p>
                            </div>
                        </div>
                    </div>

                    {notice && (
                        <div className="mt-4 rounded-[22px] bg-amber-50 border border-amber-100 px-4 py-3">
                            <p className="text-xs font-bold text-amber-700 leading-relaxed">
                                {notice}
                            </p>
                        </div>
                    )}

                    <div className="mt-4 rounded-[22px] bg-white border border-[#DDF0F6] px-4 py-3 shadow-sm">
                        <p className="text-[11px] uppercase tracking-wide text-[#6E8794] font-extrabold">
                            Current Location
                        </p>
                        <p className="text-xs text-[#0E4961] font-bold mt-1 leading-relaxed">
                            {location?.name
                                ? `📍 ${location.name}`
                                : "Location is required before you can save or submit."}
                        </p>
                        {!locked && (
                            <button
                                type="button"
                                onClick={previewLocation}
                                disabled={locating || Boolean(actionLoading)}
                                className="mt-3 h-10 px-4 rounded-[14px] bg-[#F1FAFD] border border-[#DDF0F6] text-[#09A5F7] text-xs font-extrabold disabled:opacity-60"
                            >
                                {locating ? "Getting location..." : "Capture location"}
                            </button>
                        )}
                        {locationNotice && (
                            <p className="mt-2 text-xs font-bold text-red-500">
                                {locationNotice}
                            </p>
                        )}
                    </div>

                    {banner && (
                        <div className="mt-4 rounded-[22px] bg-red-50 border border-red-100 px-4 py-3">
                            <p className="text-xs font-bold text-red-500">{banner}</p>
                            {bannerDetails.length > 1 && (
                                <ul className="mt-2 space-y-1">
                                    {bannerDetails.map((detail) => (
                                        <li
                                            key={detail}
                                            className="text-xs font-medium text-red-500"
                                        >
                                            {detail}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <div
                        ref={topProgressRef}
                        className="mt-4 rounded-[26px] bg-white border border-[#DDF0F6] p-4 shadow-[0_16px_36px_rgba(14,73,97,0.10)]"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[11px] uppercase tracking-wide text-[#6E8794] font-extrabold">
                                    Checklist Progress
                                </p>
                                <p className="text-[#0E4961] font-black text-sm mt-1">
                                    {answeredCount} of {items.length} checks completed
                                </p>
                            </div>
                            <div className="relative w-16 h-16 rounded-full bg-[#EAF8FE] flex items-center justify-center text-[#09A5F7] font-black border border-[#CDEEF9]">
                                {progress}%
                            </div>
                        </div>
                        <div className="mt-4 h-3 rounded-full bg-[#E6F3F8] overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#09A5F7] to-[#18c2dc] rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {!locked && (
                        <p className="mt-4 text-xs font-medium text-[#6E8794] leading-relaxed">
                            A remark is required for every NO. Save stores a draft for
                            this category. Submit locks this category once every
                            mandatory task is answered.
                        </p>
                    )}

                    <div className="mt-5 space-y-4">
                        {items.length === 0 && (
                            <div className="rounded-[24px] bg-white border border-[#DDF0F6] p-5 text-center">
                                <p className="text-sm font-extrabold text-[#0E4961]">
                                    No tasks in this checklist
                                </p>
                            </div>
                        )}

                        {items.map((item, index) => {
                            const isNoInvalid =
                                item.answer === "NO" && !item.remark.trim();

                            return (
                                <div
                                    id={`task-${item.id}`}
                                    key={item.id}
                                    className="rounded-[28px] bg-white border border-[#DDF0F6] shadow-[0_16px_36px_rgba(14,73,97,0.10)] p-4"
                                >
                                    <div className="flex gap-3">
                                        <div
                                            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-black shrink-0 ${
                                                item.answer
                                                    ? "bg-[#09A5F7] text-white"
                                                    : "bg-[#EAF8FE] text-[#09A5F7]"
                                            }`}
                                        >
                                            {item.answer ? "✓" : index + 1}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-[#0E4961] text-[15px] font-extrabold leading-snug">
                                                {item.title}
                                                {item.is_mandatory === 1 ? (
                                                    <span className="text-red-500"> *</span>
                                                ) : (
                                                    <span className="text-[#6E8794] text-xs font-bold">
                                                        {" "}
                                                        (optional)
                                                    </span>
                                                )}
                                            </h3>
                                        </div>
                                    </div>

                                    {locked ? (
                                        <div className="mt-4">
                                            <span
                                                className={`inline-flex h-10 items-center rounded-[14px] px-4 text-sm font-black ${
                                                    item.answer === "YES"
                                                        ? "bg-green-500 text-white"
                                                        : item.answer === "NO"
                                                          ? "bg-red-500 text-white"
                                                          : "bg-[#F1FAFD] text-[#6E8794]"
                                                }`}
                                            >
                                                {item.answer || "Not answered"}
                                            </span>
                                            {item.remark && (
                                                <p className="mt-3 text-sm text-[#0E4961] leading-relaxed">
                                                    <span className="font-extrabold">
                                                        Remark:{" "}
                                                    </span>
                                                    {item.remark}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-2 gap-3 mt-4">
                                                <button
                                                    type="button"
                                                    onClick={() => updateAnswer(item.id, "YES")}
                                                    className={`h-12 rounded-[17px] border text-sm font-black transition active:scale-[0.98] ${
                                                        item.answer === "YES"
                                                            ? "bg-green-500 border-green-500 text-white"
                                                            : "bg-green-50 border-green-200 text-green-600"
                                                    }`}
                                                >
                                                    YES
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => updateAnswer(item.id, "NO")}
                                                    className={`h-12 rounded-[17px] border text-sm font-black transition active:scale-[0.98] ${
                                                        item.answer === "NO"
                                                            ? "bg-red-500 border-red-500 text-white"
                                                            : "bg-red-50 border-red-200 text-red-500"
                                                    }`}
                                                >
                                                    NO
                                                </button>
                                            </div>

                                            {item.answer === "NO" && (
                                                <div className="mt-4">
                                                    <label className="text-xs text-[#0E4961] font-extrabold">
                                                        Remark{" "}
                                                        <span className="text-red-500">*</span>
                                                    </label>
                                                    <textarea
                                                        value={item.remark}
                                                        onChange={(event) =>
                                                            updateRemark(
                                                                item.id,
                                                                event.target.value
                                                            )
                                                        }
                                                        placeholder="Remark is required for NO"
                                                        rows={3}
                                                        className={`mt-2 w-full resize-none rounded-[20px] border bg-[#FBFEFF] px-4 py-3 text-sm text-[#0E4961] outline-none placeholder:text-[#8FA5AF] transition ${
                                                            isNoInvalid
                                                                ? "border-red-300 focus:border-red-500"
                                                                : "border-[#DDF0F6] focus:border-[#09A5F7]"
                                                        }`}
                                                    />
                                                    {isNoInvalid && (
                                                        <p className="mt-2 text-xs font-bold text-red-500">
                                                            Remark is required when NO is selected.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] bg-white/95 backdrop-blur-xl border-t border-[#DDF0F6] px-5 py-4">
                    {locked ? (
                        <button
                            type="button"
                            onClick={() => navigate("/home")}
                            className="w-full h-14 rounded-[18px] bg-gradient-to-r from-[#09A5F7] to-[#18c2dc] text-white font-black"
                        >
                            Back to checklists
                        </button>
                    ) : (
                        <div className="flex items-center gap-3">
                            <div
                                className={`relative overflow-hidden transition-all duration-500 ease-in-out ${
                                    showBottomProgress
                                        ? "w-14 opacity-100 mr-0"
                                        : "w-0 opacity-0 -mr-3"
                                }`}
                            >
                                <div className="relative w-14 h-14 rounded-full bg-[#EAF8FE] border border-[#CDEEF9] flex items-center justify-center shrink-0">
                                    <svg className="absolute inset-0 w-full h-full -rotate-90">
                                        <circle
                                            cx="28"
                                            cy="28"
                                            r="23"
                                            stroke="#ffffff"
                                            strokeWidth="5"
                                            fill="none"
                                        />
                                        <circle
                                            cx="28"
                                            cy="28"
                                            r="23"
                                            stroke="#09A5F7"
                                            strokeWidth="5"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeDasharray={2 * Math.PI * 23}
                                            strokeDashoffset={
                                                2 * Math.PI * 23 -
                                                (progress / 100) * (2 * Math.PI * 23)
                                            }
                                        />
                                    </svg>
                                    <div className="relative z-10 text-center">
                                        <p className="text-[#09A5F7] text-sm font-black leading-none">
                                            {remainingTasks}
                                        </p>
                                        <p className="text-[8px] text-[#6E8794] font-bold mt-0.5">
                                            left
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => saveChecklist("DRAFT")}
                                    disabled={Boolean(actionLoading) || locating}
                                    className="flex-1 h-14 rounded-[18px] border-2 border-[#09A5F7] bg-white text-[#09A5F7] font-black text-sm disabled:opacity-60"
                                >
                                    {actionLoading === "DRAFT" || (locating && actionLoading === "DRAFT")
                                        ? "Saving..."
                                        : "Save"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => saveChecklist("SUBMITTED")}
                                    disabled={!canSubmit || Boolean(actionLoading) || locating}
                                    className={`flex-[1.3] h-14 rounded-[18px] font-black text-base transition-all ${
                                        canSubmit && !actionLoading && !locating
                                            ? "bg-gradient-to-r from-[#09A5F7] to-[#18c2dc] text-white"
                                            : "bg-[#F1FAFD] text-[#8FA5AF] border border-[#DDF0F6]"
                                    }`}
                                >
                                    {actionLoading === "SUBMITTED"
                                        ? "Submitting..."
                                        : "Submit ✓"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
}
